import { cva, type VariantProps } from "class-variance-authority";
import { Column, Container, Row } from "../../layout/grid";
import { cn } from "../../lib/utils";
import type { ImageSectionInspectorProps } from "./image-section.preview";

const WIDE_COLUMN_WIDTH = 12;

const imageSectionVariants = cva("", {
  variants: {
    fullWidth: {
      true: "",
      false: "",
    },
    columnWidth: {
      8: "col-span-10 col-start-2 md:col-span-8 md:col-start-3",
      10: "col-span-10 col-start-2",
      12: "col-span-12",
    },
    aspectRatio: {
      "21:9": "aspect-21/9",
      "16:9": "aspect-video",
    },
    backgroundColor: {
      black: "bg-black",
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
    },
  },
  defaultVariants: {
    fullWidth: false,
    aspectRatio: "16:9",
    columnWidth: 12,
    backgroundColor: "white",
  },
});

export interface ImageSectionProps
  extends VariantProps<typeof imageSectionVariants> {
  sys?: { id: string };
  fullWidth?: boolean;
  columnWidth?: VariantProps<typeof imageSectionVariants>["columnWidth"];
  backgroundColor?: "black" | "white" | "whitesmoke";
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  image?: {
    sys?: { id: string };
    imageDesktop?: {
      src?: string;
      srcSet?: string;
    };
    imageMobile?: {
      src?: string;
      srcSet?: string;
    };
    sizes?: string;
    className?: string;
    alt?: string;
  };
  className?: string;
  inspectorProps?: ImageSectionInspectorProps;
}

export function ImageSection({
  backgroundColor,
  aspectRatio,
  columnWidth,
  inspectorProps,
  ...props
}: Readonly<ImageSectionProps>) {
  const variantContainerClasses = imageSectionVariants({
    aspectRatio: null,
    columnWidth: null,
    backgroundColor,
  });

  const variantImageClasses = imageSectionVariants({
    aspectRatio,
    columnWidth: null,
    backgroundColor: null,
  });

  const columnClasses = imageSectionVariants({
    columnWidth,
    backgroundColor: null,
    aspectRatio: null,
  });

  // Use placeholder.svg as fallback if no images are provided
  const desktopSrc =
    props.image?.imageDesktop?.src || "/assets/placeholder.svg";
  const mobileSrc = props.image?.imageMobile?.src || desktopSrc;
  const desktopSrcSet = props.image?.imageDesktop?.srcSet;
  const mobileSrcSet = props.image?.imageMobile?.srcSet;
  const sizes = props.image?.sizes;
  const alt = props.image?.alt || "Scott image alt text";

  const picture = (
    <picture>
      <source
        media="(min-width: 768px)"
        sizes={desktopSrcSet ? sizes : undefined}
        srcSet={desktopSrcSet || desktopSrc}
      />
      <img
        alt={alt}
        className={cn(
          variantImageClasses,
          props.image?.className,
          "h-full w-full object-cover object-center"
        )}
        loading="lazy"
        sizes={mobileSrcSet ? sizes : undefined}
        src={mobileSrc}
        srcSet={mobileSrcSet}
      />
    </picture>
  );

  // "wide" spans the whole 12-column grid, so it is edge to edge like the
  // intro section and carries the page padding itself.
  const isWide = columnWidth === WIDE_COLUMN_WIDTH;

  return (
    <Container
      className={cn(variantContainerClasses, "component-image-section")}
      fullWidth={true}
      outerContainerOnly={props.fullWidth || isWide}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      testId="image-section"
      {...inspectorProps?.container}
    >
      {props.fullWidth ? (
        picture
      ) : (
        <Row className={cn(isWide && "px-3 md:px-6 lg:px-8 xl:px-11.5")}>
          <Column className={cn(columnClasses)}>{picture}</Column>
        </Row>
      )}
    </Container>
  );
}
